/*
 * Copyright (c) 2024 Amazon.com, Inc. All Rights Reserved.
 */
import "./CommitHistoryTable.css";
import { formatUTCDate } from "../../config/DateUtils";
import Select from "react-select";
import { Region } from "../../model/Region";
import { DetectionSystem } from "../../model/DetectionSystem";
import { CommitEntry } from "../../model/CommitEntry";
import { CommitType } from "../../model/CommitType";
import DateFilter from "../filters/DateFilter";
import NumberFilter from "../filters/NumberFilter";
import { useState } from "react";
import { DateFilterValues, NumberFilterValues } from "../../model/FilterValues";

/**
 * @author stcatai Mar 22, 2024
 */
interface CommitHistoryTableProps {
  commits: CommitEntry[];
  setRegionFetch: any;
  setDetectionSystemFetch: any;
  setCommitTypeFetch: any;
  setPublishableFetch: any;
  setLabelFetch: any;
  setModelNameFetch: any;
  setDescriptionFetch: any;
  setAuthorFetch: any;
  setTtlFetch: any;
  setCommitDateFetch: any;
}

export function CommitHistoryTable(props: CommitHistoryTableProps) {

  const REGION_OPTIONS = ["All", ...Object.values(Region)].map(region => ({value: region, label: region}));
  const TYPE_OPTIONS = ["All", ...Object.values(CommitType)].map(option => ({value: option, label: option}));
  const DETECTION_SYSTEM_OPTIONS = ["All", ...Object.values(DetectionSystem)].map(system => ({value: system, label: system}));
  const PUBLISHABLE_OPTIONS = ["All", "true", "false"].map(option => ({value: option, label: option}));

  const [commitDateFilter, setCommitDateFilter] = useState<DateFilterValues>({});
  const [regionFilter, setRegionFilter] = useState<string>("");
  const [authorFilter, setAuthorFilter] = useState<string>("");
  const [descriptionFilter, setDescriptionFilter] = useState<string>("");
  const [commitTypeFilter, setCommitTypeFilter] = useState<string>("");
  const [detectionSystemFilter, setDetectionSystemFilter] = useState<string>("");
  const [modelNameFilter, setModelNameFilter] = useState<string>("");
  const [publishableFilter, setPublishableFilter] = useState<string>("");
  const [ttlFilter, setTtlFilter] = useState<NumberFilterValues>({});
  const [labelsFilter, setLabelsFilter] = useState<string>("");

  const setSelectValues = (updatedValue: any, setFilter: any, setFetch: any) => {
    let value = updatedValue?.value || "";
    if (value === "All")
      value = "";
    setFilter(value);
    setFetch(value);
  }

  const setOnKeyDown = (event: any, setFetch: any, value: any) => {
    if (event.key === "Enter")
      setFetch(value);
  }

  return <table className="table table-striped table-bordered">
    <thead>
      <tr>
        <th>Date (UTC)</th>
        <th>Region</th>
        <th>Author</th>
        <th>Description</th>
        <th>Type</th>
        <th>Detection System</th>
        <th>Model Name</th>
        <th>Publishable</th>
        <th>TTL (mins)</th>
        <th>Labels</th>
      </tr>
      <tr className="filters">
        <td><DateFilter values={commitDateFilter} setValues={setCommitDateFilter} setFetch={props.setCommitDateFetch}/></td>
        <td><Select options={REGION_OPTIONS} value={{value: regionFilter, label: regionFilter}} onChange={newValue => setSelectValues(newValue, setRegionFilter, props.setRegionFetch)}/></td>
        <td><input type="text" value={authorFilter} onChange={e => setAuthorFilter(e.target.value)} onKeyDown={(e) => setOnKeyDown(e, props.setAuthorFetch, authorFilter)}/></td>
        <td><input type="text" value={descriptionFilter} onChange={e => setDescriptionFilter(e.target.value)} onKeyDown={(e) => setOnKeyDown(e, props.setDescriptionFetch, descriptionFilter)}/></td>
        <td><Select options={TYPE_OPTIONS} value={{value: commitTypeFilter, label: commitTypeFilter}} onChange={newValue => setSelectValues(newValue, setCommitTypeFilter, props.setCommitTypeFetch)}/></td>
        <td><Select options={DETECTION_SYSTEM_OPTIONS} value={{value: detectionSystemFilter, label: detectionSystemFilter}} onChange={newValue => setSelectValues(newValue, setDetectionSystemFilter, props.setDetectionSystemFetch)}/></td>
        <td><input type="text" value={modelNameFilter} onChange={e => setModelNameFilter(e.target.value)} onKeyDown={(e) => setOnKeyDown(e, props.setModelNameFetch, modelNameFilter)}/></td>
        <td><Select options={PUBLISHABLE_OPTIONS} value={{value: publishableFilter, label: publishableFilter}} onChange={newValue => setSelectValues(newValue, setPublishableFilter, props.setPublishableFetch)}/></td>
        <td><NumberFilter values={ttlFilter} setValues={setTtlFilter} setFetch={props.setTtlFetch}/></td>
        <td><input type="text" value={labelsFilter} onChange={e => setLabelsFilter(e.target.value)} onKeyDown={(e) => setOnKeyDown(e, props.setLabelFetch, labelsFilter)}/></td>
      </tr>
    </thead>
    <tbody>
      {
        props.commits.map((commit, idx) => <tr key={idx}>
          <td>{formatUTCDate(commit.timestamp)}</td>
          <td><span onClick={() => {setRegionFilter(commit.region); props.setRegionFetch(commit.region);}} className="clickable">{commit.region}</span></td>
          <td><span onClick={() => {setAuthorFilter(commit.author); props.setAuthorFetch(commit.author);}} className="clickable">{commit.author}</span></td>
          <td className="description">{commit.description}</td>
          <td><span onClick={() => {setCommitTypeFilter(commit.type); props.setCommitTypeFetch(commit.type);}} className="clickable">{commit.type}</span></td>
          <td><span onClick={() => {setDetectionSystemFilter(commit.entryDetectionSystem); props.setDetectionSystemFetch(commit.entryDetectionSystem);}} className="clickable">{commit.entryDetectionSystem}</span></td>
          <td><span onClick={() => {setModelNameFilter(commit.entryModelName); props.setModelNameFetch(commit.entryModelName);}} className="clickable">{commit.entryModelName}</span></td>
          <td><span onClick={() => {setPublishableFilter(commit.entryPublishable.valueOf().toString()); props.setPublishableFetch(commit.entryPublishable.valueOf().toString());}} className="clickable">{commit.entryPublishable.valueOf().toString()}</span></td>
          <td>{commit.entryTtl}</td>
          <td>{commit.entryLabels && Array.from(commit.entryLabels).map((label, labelIdx) =>
            <span key={labelIdx} onClick={() => {setLabelsFilter(label); props.setLabelFetch(label);}} className="clickable">{label}</span>)}
          </td>
        </tr>)
      }
    </tbody>
  </table>
}